import React, { CSSProperties, useEffect } from 'react';
import { MediumHeader } from '@narvar/nth-bit-header';
import { useTheme, withThemeProvider } from '@narvar/nth-hook-theme';
import CheckMarkIcon from '@narvar/nth-bit-icon-check-mark';
import { Column, Row } from '@narvar/nth-grid';
import { RetailerTheme } from '@narvar/nth-hook-loma';
import { usePageContext } from '@narvar/nth-hook-context-page';
import { useTitanAnalytics } from '../../hooks/useTitanAnalytics';

interface DeliveryInstructionsSublimeSuccessProps {
  autoApplied?: boolean;
  hasProfile?: boolean;
}

const DeliveryInstructionsSublimeSuccess = ({
  autoApplied,
  hasProfile,
}: DeliveryInstructionsSublimeSuccessProps) => {
  const {
    state: { trackingNumbers, orderNumbers },
  } = usePageContext();
  const { bodytextDefaultNone, linksTrackingDefault } = useTheme() as {
    [key in keyof RetailerTheme]: CSSProperties;
  };

  const titan = useTitanAnalytics();

  useEffect(() => {
    if (!titan) return;
    if (autoApplied) return;

    titan
      ?.track({
        event: 'promotion_clicked',
        channel: 'web',
        type: 'track',
        context: {},
        properties: {
          promotion_id: 'delivery-preferences',
          narvar_product_context: 'track',
          variant: 'subscribed',
          extra_properties: [
            {
              key: 'orderId',
              value: orderNumbers?.[0],
            },
            {
              key: 'trackingNumber',
              value: trackingNumbers?.[0],
            },
          ],
        },
      })
      // eslint-disable-next-line no-console
      .catch((e) => console.error(e));
  }, [trackingNumbers, orderNumbers, titan, autoApplied]);

  // Temp translation text
  const deliveryInstructionsAppliedSuccessfully =
    'Delivery Instructions Applied Successfully';
  const deliveryInstructionsAppliedCompleteProfile =
    'We’ve saved your preferred delivery instructions. Complete your profile to apply your preferences to future orders.';
  const deliveryInstructionsAppliedToFutureOrders =
    'We’ve saved your preferred delivery instructions for future orders.';
  const deliveryInstructionsAutoApplied =
    'Default Delivery Instructions Applied Successfully';
  const deliveryInstructionsAppliedPreferred =
    'You can manage your delivery preferences in your profile page.';
  const deliveryInstructionsCompleteProfile = 'Complete profile';
  const deliveryInstructionsManagePreferences = 'Manage preferences';

  const successTitle = autoApplied
    ? deliveryInstructionsAutoApplied
    : deliveryInstructionsAppliedSuccessfully;

  const successText = () => {
    if (autoApplied) return deliveryInstructionsAppliedPreferred;
    if (hasProfile) return deliveryInstructionsAppliedToFutureOrders;
    return deliveryInstructionsAppliedCompleteProfile;
  };

  const profileLink =
    !hasProfile && !autoApplied
      ? deliveryInstructionsCompleteProfile
      : deliveryInstructionsManagePreferences;

  return (
    <div style={{ padding: '0.25rem 0' }}>
      <Row>
        <Column xs-style="display: block;">
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div>
              <CheckMarkIcon />
            </div>
            <MediumHeader
              as="h2"
              containerCustomStyle={{ margin: '0 0 0 1rem' }}
            >
              {successTitle}
            </MediumHeader>
          </div>
          <div style={{ marginTop: '1rem' }}>
            <span style={{ ...bodytextDefaultNone }}>
              {successText()}{' '}
              <a
                href="https://profile.narvar.com"
                target="_blank"
                rel="noreferrer"
                style={{ ...linksTrackingDefault }}
              >
                {profileLink}
              </a>
              .
            </span>
          </div>
        </Column>
      </Row>
    </div>
  );
};

export default withThemeProvider(DeliveryInstructionsSublimeSuccess, {
  themeTokens: ['bodytextDefaultNone', 'linksTrackingDefault'],
});
